
import { useState } from 'react';

interface CalculatorInputs {
  responseType: 'scheduled' | 'expedited' | 'priority';
  estimatedHours: number;
}

interface PricingBreakdown {
  baseFee: number;
  expeditedFee: number;
  additionalHours: number;
  additionalHoursCost: number;
  subtotal: number;
  priorityPackageCost: number;
  savings: number;
  isPriorityPackage: boolean;
}

export default function RecoveryPriceCalculator() {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    responseType: 'scheduled',
    estimatedHours: 1,
  });

  // Fixed arrow‑function syntax
  const calculatePricing = (): PricingBreakdown => {
    if (inputs.responseType === 'priority') {
      const standardCost =
        395 + 295 + Math.max(0, inputs.estimatedHours - 1) * 295;
      return {
        baseFee: 0,
        expeditedFee: 0,
        additionalHours: 0,
        additionalHoursCost: 0,
        subtotal: 1000,
        priorityPackageCost: 1000,
        savings: Math.max(0, standardCost - 1000),
        isPriorityPackage: true,
      };
    }

    const baseFee = 395;
    const expeditedFee = inputs.responseType === 'expedited' ? 295 : 0;
    const additionalHours = Math.max(0, inputs.estimatedHours - 1);
    const additionalHoursCost = additionalHours * 295;
    const subtotal = baseFee + expeditedFee + additionalHoursCost;

    return {
      baseFee,
      expeditedFee,
      additionalHours,
      additionalHoursCost,
      subtotal,
      priorityPackageCost: 1000,
      savings: 0,
      isPriorityPackage: false,
    };
  };

  const pricing = calculatePricing();

  return (
    <section className="py-20 px-6 bg-stone-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Recovery Service Pricing
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Transparent pricing for time-sensitive recovery missions. Choose the
            service level that matches your urgency and expected search
            complexity.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <h3 className="text-xl font-bold mb-6">
              Calculate Your Recovery Cost
            </h3>

            {/* Response Type */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Response Type
              </label>
              <div className="space-y-3">
                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                    inputs.responseType === 'scheduled'
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="responseType"
                    value="scheduled"
                    checked={inputs.responseType === 'scheduled'}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        responseType: e.target.value as CalculatorInputs['responseType'],
                      })
                    }
                    className="mt-1 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-medium text-gray-900">
                      Scheduled Response
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      On-site within 48 hours
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                    inputs.responseType === 'expedited'
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="responseType"
                    value="expedited"
                    checked={inputs.responseType === 'expedited'}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        responseType: e.target.value as CalculatorInputs['responseType'],
                      })
                    }
                    className="mt-1 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-medium text-gray-900">
                      Expedited Response
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      On-site within 3 hours
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 relative ${
                    inputs.responseType === 'priority'
                      ? 'border-emerald-700 bg-emerald-50'
                      : 'border-emerald-300 bg-emerald-50/30'
                  }`}
                >
                  <div className="absolute -top-3 right-4 bg-emerald-700 text-white text-xs font-bold px-3 py-1 rounded-full">
                    BEST OPTION
                  </div>
                  <input
                    type="radio"
                    name="responseType"
                    value="priority"
                    checked={inputs.responseType === 'priority'}
                    onChange={(e) =>
                      setInputs({
                        ...inputs,
                        responseType: e.target.value as CalculatorInputs['responseType'],
                      })
                    }
                    className="mt-1 text-emerald-700 focus:ring-emerald-600 cursor-pointer"
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-bold text-gray-900">
                      Priority Recovery Package
                    </div>
                    <div className="text-sm text-gray-700 mt-1 font-medium">
                      Expedited response + up to 5 hours search time
                    </div>
                    <div className="text-sm text-emerald-800 mt-2 font-medium">
                      $1,000 flat fee — Save up to $870
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Estimated Hours */}
            {inputs.responseType !== 'priority' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Estimated Search Time Needed
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4, 5].map((hours) => (
                    <button
                      key={hours}
                      type="button"
                      onClick={() =>
                        setInputs({ ...inputs, estimatedHours: hours })
                      }
                      className={`px-4 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        inputs.estimatedHours === hours
                          ? 'bg-emerald-800 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {hours}h
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  First hour included in base fee. Additional hours billed at
                  $295/hour.
                </p>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">Why Advance Payment?</p>
                  <p className="text-blue-800">
                    Recovery missions require immediate mobilization. Advance
                    payment allows us to prioritize your case and deploy without
                    delay.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Breakdown */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
            <h3 className="text-xl font-bold mb-6">Your Recovery Cost</h3>

            {pricing.isPriorityPackage ? (
              <>
                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-sm text-emerald-800 font-medium mb-1">
                        Priority Recovery Package
                      </div>
                      <div className="text-3xl font-bold text-emerald-900">
                        $1,000
                      </div>
                    </div>
                    <div className="w-12 h-12 bg-emerald-700 rounded-full flex items-center justify-center">
                      <i className="ri-shield-check-line text-2xl text-white"></i>
                    </div>
                  </div>
                  <div className="text-sm text-emerald-800 space-y-2">
                    <div className="flex items-center">
                      <i className="ri-check-line text-lg mr-2"></i>
                      <span>Expedited response (on-site within 3 hours)</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-lg mr-2"></i>
                      <span>Up to 5 hours of continuous search time</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-check-line text-lg mr-2"></i>
                      <span>Priority scheduling and mobilization</span>
                    </div>
                  </div>
                </div>

                {pricing.savings > 0 && (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-6">
                    <div className="text-sm text-gray-600 mb-2">
                      Compared to standard hourly pricing:
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">
                        Standard cost for {inputs.estimatedHours} hours:
                      </span>
                      <span className="text-gray-500 line-through">
                        ${(1000 + pricing.savings).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between font-bold text-emerald-700 mt-1">
                      <span>Your savings:</span>
                      <span>${pricing.savings.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-gray-700">Base recovery service fee</span>
                    <span className="font-medium">
                      ${pricing.baseFee.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 -mt-2 pl-4">
                    Includes first hour of search time
                  </div>

                  {pricing.expeditedFee > 0 && (
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-700">Expedited response</span>
                      <span className="font-medium">
                        ${pricing.expeditedFee.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {pricing.additionalHours > 0 && (
                    <>
                      <div className="flex items-center justify-between py-2">
                        <span className="text-gray-700">
                          Additional time ({pricing.additionalHours} hours)
                        </span>
                        <span className="font-medium">
                          ${pricing.additionalHoursCost.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 -mt-2 pl-4">
                        $295 per hour beyond first hour
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t border-gray-200 pt-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-gray-900">
                      Total Cost
                    </span>
                    <span className="text-2xl font-bold text-emerald-800">
                      ${pricing.subtotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Priority Package Comparison */}
                {inputs.estimatedHours >= 2 &&
                  pricing.subtotal > 1000 && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <i className="ri-lightbulb-line text-emerald-700 text-lg mt-0.5"></i>
                        <div className="text-sm">
                          <p className="font-medium text-emerald-900 mb-1">
                            Consider Priority Recovery Package
                          </p>
                          <p className="text-emerald-800">
                            Save $
                            {(pricing.subtotal - 1000).toFixed(2)} with our
                            flat-rate package, which includes expedited response
                            and up to 5 hours of search time.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
              </>
            )}

            {/* Payment Terms */}
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-gray-900 mb-2">Payment Terms</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-700 mt-0.5"></i>
                  <span>Payment required in advance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-700 mt-0.5"></i>
                  <span>Credit card accepted</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-emerald-700 mt-0.5"></i>
                  <span>Scheduling confirmed after payment</span>
                </li>
              </ul>
            </div>

            {/* CTA Button */}
            <a
              href="#urgent-form"
              className="block w-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold py-4 px-8 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-center mt-6"
            >
              Request Recovery Service
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-3">Important Information</h4>
          <div className="text-sm text-gray-700 space-y-2">
            <p>
              <strong>Recovery outcomes are not guaranteed.</strong> Success
              depends on timing, environmental conditions, terrain, and the
              specific circumstances of each case. Our team will provide a
              realistic assessment of recovery probability before deployment.
            </p>
            <p>
              We maintain a 90% recovery success rate through advanced thermal
              imaging technology, AI-assisted search methods, and specialized
              training. This exceeds typical outcomes, but no recovery service
              can guarantee results in every situation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
